import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap, catchError, of } from 'rxjs';
import { API_BASE_URL } from '../../config/api.constants';
import type { Notification, NotificationCount } from '../../models/notification.model';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${API_BASE_URL}/notifications`;

  readonly unreadCount = signal<number>(0);

  readonly hasError = signal<boolean>(false);

  refreshCount(): void {
    this.http
      .get<{ data: NotificationCount }>(`${this.baseUrl}/count`)
      .pipe(catchError(() => of(null)))
      .subscribe(res => {
        if (res?.data) {
          this.unreadCount.set(res.data.unread);
          this.hasError.set(res.data.hasError);
        }
      });
  }

  listRaw(params?: {
    status?: 'unread' | 'all';
    limit?: number;
    category?: string;
  }): Observable<{ data: Notification[] }> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.category) qs.set('category', params.category);
    const query = qs.toString() ? `?${qs.toString()}` : '';

    return this.http
      .get<{ data: Notification[] }>(`${this.baseUrl}${query}`)
      .pipe(catchError(() => of({ data: [] as Notification[] })));
  }

  markRead(documentId: string): Observable<{ data: Notification }> {
    return this.http.post<{ data: Notification }>(`${this.baseUrl}/${documentId}/read`, {}).pipe(
      tap(() => this.refreshCount()),
      catchError(() => of({ data: {} as Notification })),
    );
  }

  markAllRead(): Observable<{ data: { updated: number } }> {
    return this.http.post<{ data: { updated: number } }>(`${this.baseUrl}/read-all`, {}).pipe(
      tap(() => this.refreshCount()),
      catchError(() => of({ data: { updated: 0 } })),
    );
  }
}
